import { useEffect, useRef } from 'react'

export const useFormPersistence = (formMethods, formKey, isEnabled = false) => {
  const { getValues, reset, setValue } = formMethods
  const persistenceKey = `form-persistence-${formKey}`
  const isInitialized = useRef(false)

  // Save form data to localStorage
  const saveFormData = () => {
    if (!isEnabled) return
    
    try {
      const formData = getValues()
      const dataToSave = {
        ...formData,
        _timestamp: Date.now()
      }
      localStorage.setItem(persistenceKey, JSON.stringify(dataToSave))
    } catch (error) {
      console.warn('Failed to save form data:', error)
    }
  }

  // Load form data from localStorage
  const loadFormData = () => {
    if (!isEnabled) return null
    
    try {
      const savedData = localStorage.getItem(persistenceKey)
      if (savedData) {
        const parsedData = JSON.parse(savedData)
        const { _timestamp, ...formData } = parsedData
        
        // Only load data if it's less than 1 hour old
        if (Date.now() - _timestamp < 3600000) {
          return formData
        }
      }
    } catch (error) {
      console.warn('Failed to load form data:', error)
    }
    return null
  }

  // Clear persisted data
  const clearPersistedData = () => {
    try {
      localStorage.removeItem(persistenceKey)
    } catch (error) {
      console.warn('Failed to clear persisted data:', error)
    }
  }

  // Initialize form with persisted data
  useEffect(() => {
    if (!isInitialized.current && isEnabled) {
      const savedData = loadFormData()
      if (savedData) {
        // Use setTimeout to ensure form is ready
        setTimeout(() => {
          Object.entries(savedData).forEach(([key, value]) => {
            setValue(key, value)
          })
        }, 0)
      }
      isInitialized.current = true
    }
  }, [setValue, isEnabled])

  // Auto-save on form changes
  useEffect(() => {
    if (!isEnabled || !isInitialized.current) return

    const interval = setInterval(saveFormData, 5000) // Save every 5 seconds
    return () => clearInterval(interval)
  }, [isEnabled])

  // Save on unmount
  useEffect(() => {
    return () => {
      if (isEnabled && isInitialized.current) {
        saveFormData()
      }
    }
  }, [isEnabled])

  return {
    saveFormData,
    loadFormData,
    clearPersistedData,
    // For debugging purposes
    getPersistedData: () => loadFormData(),
    persistenceKey
  }
} 